import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { DateTime } from "luxon";
import { convertAmmountToWords } from "services";

export const generateSaleCertificate100PerPDF = async (
  eventData: any,
  highestBid: any,
  bidders: any[],
  bank?: any
) => {
  // === 1. Data prep (same as your code, just kept as-is) ===

  const bidderEmail = highestBid?.meta?.email || highestBid?.user_email;
  const highestBidder = bidders.find(
    (b) => b.email === bidderEmail || b.user_uuid === highestBid?.user_uuid
  );

  const bidderName =
    highestBidder?.meta?.name ||
    highestBid?.meta?.name ||
    highestBid?.bidder_name ||
    "N/A";

  const bidderAddress = highestBidder?.meta?.user_details?.address || "";
  const addressLines = bidderAddress ? bidderAddress.split("\n") : [];
  const addressLine1 = addressLines[0] || "";
  const addressLine2 = addressLines.slice(1).join(", ") || "";

  const eventId = eventData?.listing_id || "N/A";
  const borrowerName = eventData?.borrower_name || "N/A";
  const bankName =
    eventData?.property_dump?.bank?.name || eventData?.bank_name || "N/A";
  const propertyDescription =
    eventData?.property_dump?.sub_asset_type?.name ||
    eventData?.property_type ||
    "N/A";
  const fullPropertyDescription =
    eventData?.property_dump?.description || "N/A";
  const auctionDate = eventData?.start_date_time
    ? DateTime.fromISO(eventData.start_date_time)
        .setZone("Asia/Kolkata")
        .toFormat("dd/MM/yyyy")
    : "N/A";
  const finalBidAmount = highestBid?.final_amount
    ? `₹${parseFloat(highestBid.final_amount).toLocaleString("en-IN")}`
    : "N/A";

  const currentDate = DateTime.now()
    .setZone("Asia/Kolkata")
    .toFormat("dd/MM/yyyy");

  const finalBidAmountInWords = convertAmmountToWords(highestBid?.final_amount);

  const bankLogoUrl = bank?.logo?.key_path
    ? `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${bank.logo.key_path}`
    : null;

  // === 2. Create hidden A4-like container ===
  // A4 @ ~96dpi ≈ 794 × 1123 px. We’ll use 794 and add padding inside.
  const tempDiv = document.createElement("div");
  tempDiv.style.position = "fixed";
  tempDiv.style.left = "-9999px";
  tempDiv.style.top = "0";
  tempDiv.style.width = "794px"; // content width for A4
  tempDiv.style.minHeight = "1123px"; // full A4 height
  //   tempDiv.style.padding = "60px 70px"; // visual margins inside the canvas
  tempDiv.style.boxSizing = "border-box";
  tempDiv.style.fontFamily = "'Times New Roman', serif";
  tempDiv.style.fontSize = "11pt";
  tempDiv.style.lineHeight = "1.5";
  tempDiv.style.color = "#000";
  tempDiv.style.backgroundColor = "#ffffff";

  tempDiv.innerHTML = `
    <!-- Header -->
    <div style="width: 100%; margin-bottom: 24px;">
      <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
        <div style="display: flex; justify-content: flex-start; align-items: center; flex: 1;">
          ${
            bankLogoUrl
              ? `<img src="${bankLogoUrl}" alt="Bank Logo" style="max-width: 180px; max-height: 60px; object-fit: contain;" />`
              : ""
          }
        </div>
      </div>
      <div style="display: flex; width: 100%; margin-top: 8px;">
        <div style="flex-basis: 50%; height: 3px; background-color: #F27E1F;"></div>
        <div style="flex-basis: 50%; height: 3px; background-color: #e5e7eb;"></div>
      </div>
    </div>

    <!-- Title -->
    <div style="text-align: center; font-weight: bold; font-size: 14pt; margin-bottom: 28px; text-decoration: underline;">
    Sale Certificate (100% Payment)
    </div>

    <!-- Date -->
    <div style="text-align: right; margin-bottom: 24px; font-size: 11pt;">
      Date: ${currentDate}
    </div>

    <!-- Address block -->
    <div style="margin-bottom: 24px; font-size: 11pt;">
      <div style="margin-bottom: 6px;">To,</div>
      <div style="margin-bottom: 4px;"><strong>${bidderName}</strong></div>
      ${
        addressLine1
          ? `<div style="margin-bottom: 4px;">${addressLine1}</div>`
          : ""
      }
      ${
        addressLine2
          ? `<div style="margin-bottom: 4px;">${addressLine2}</div>`
          : ""
      }
    </div>

    <div style="margin-bottom: 16px;">Dear Sir/Madam,</div>

    <!-- Subject -->
    <div style="font-weight: bold; margin-bottom: 20px; text-align: justify;">
      Sub: Sale Certificate issued under the SARFAESI Act – ${propertyDescription} – Event ID ${eventId}
    </div>

    <!-- Meta -->

    <div style="margin-bottom: 8px; text-align: justify;">
      <strong>Borrower Name:</strong> ${borrowerName}
    </div>

    <div style="margin-bottom: 8px; text-align: justify;">
      <strong>Auction Date:</strong> ${auctionDate}
    </div>

    <div style="margin-bottom: 20px; text-align: justify;">
      <strong>Final Bid:</strong> ${finalBidAmount}
    </div>

    <!-- Body paragraphs -->
    <div style="margin-bottom: 12px; text-align: justify;">
      WHEREAS, pursuant to the demand notice issued under Section 13(2) of the SARFAESI Act and possession taken under Section 13(4) thereof, the below described secured asset was brought to sale by way of e-auction on AuctionBazaar.com platform; and
    </div>

    <div style="margin-bottom: 12px; text-align: justify;">
     WHEREAS, you have remitted the entire sale consideration of <b>${finalBidAmount}</b> (Rupees <b style="text-transform: capitalize;">${finalBidAmountInWords}</b>) within the prescribed time;
    </div>

    <div style="margin-bottom: 12px; text-align: justify;">
     NOW THEREFORE, in exercise of the powers conferred under the SARFAESI Act and in consideration of the said payment, the Secured Creditor hereby confirms the sale and conveys to you all the rights, title and interest of the Secured Creditor in the said property, free from all encumbrances known to the Secured Creditor, on “as is where is” and “as is what is” basis.
    </div>

    <div style="margin-bottom: 12px; text-align: justify;">
    Description of property: ${fullPropertyDescription}
    </div>

    <div style="margin-bottom: 12px; text-align: justify;">
      You are hereby authorised to get the property transferred / mutated in your favour in the records of the concerned Sub-Registrar / Revenue Authorities / Society / Association, at your cost and risk.
    </div>
    
    <div style="margin-bottom: 12px; text-align: justify;">
      This Sale Certificate is issued in duplicate.
    </div>

    <!-- Footer -->
    <div style="margin-top: 50px; font-size: 11pt;">
      <div style="margin-bottom: 6px;">Yours faithfully,</div>
      <div style="margin-top: 40px;">
        <div style="margin-bottom: 4px;"><strong>Authorised Officer / Authorised Signatory</strong></div>
        <div style="margin-bottom: 4px;"><strong>${bankName}</strong></div>
        <div>Through AuctionBazaar.com platform</div>
      </div>
    </div>
  `;

  document.body.appendChild(tempDiv);

  // Wait for layout
  await new Promise((resolve) => setTimeout(resolve, 50));

  try {
    // === 3. Capture with html2canvas using the real width of the element ===
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // higher resolution
      useCORS: true,
      logging: false,
      width: tempDiv.offsetWidth,
      height: tempDiv.offsetHeight,
      backgroundColor: "#ffffff",
    });

    document.body.removeChild(tempDiv);

    // === 4. Create PDF with margins (A4) ===
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth(); // 210
    const pageHeight = pdf.internal.pageSize.getHeight(); // 297
    const marginLeftRight = 15; // mm
    const marginTop = 15; // mm

    const imgWidth = pageWidth - marginLeftRight * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const imgData = canvas.toDataURL("image/png");

    let heightLeft = imgHeight;
    let position = marginTop;

    pdf.addImage(
      imgData,
      "PNG",
      marginLeftRight,
      position,
      imgWidth,
      imgHeight
    );
    heightLeft -= pageHeight - marginTop;

    // Extra pages if content runs over (shouldn’t in your case, but safe)
    while (heightLeft > 0) {
      pdf.addPage();
      position = marginTop - (imgHeight - heightLeft);
      pdf.addImage(
        imgData,
        "PNG",
        marginLeftRight,
        position,
        imgWidth,
        imgHeight
      );
      heightLeft -= pageHeight - marginTop;
    }

    const filename = `Sale_Certificate_100_Percent_${eventId}_${DateTime.now().toFormat(
      "yyyyMMdd"
    )}.pdf`;

    pdf.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    if (document.body.contains(tempDiv)) {
      document.body.removeChild(tempDiv);
    }
    throw error;
  }
};
